'use client';

import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSharedUI } from './ui-adapter';

/** Describes a navigable item in a sheet list */
export interface SheetNavigationItem {
  /** Unique identifier for this item (used to track current position) */
  id: string;
  /** Title shown in the sheet header */
  title: string;
}

/** Navigation list config — allows prev/next browsing through a set of items */
export interface SheetNavigationList {
  /** The ordered list of navigable items */
  items: SheetNavigationItem[];
  /** Called when the user navigates to a different item. Should return the sheet content for that item. */
  renderContent: (item: SheetNavigationItem) => ReactNode;
  /** Optional: max width override per item (defaults to the sheet's maxWidth) */
  maxWidth?: string;
}
interface SheetConfig {
  id: string;
  title: string;
  content: ReactNode;
  maxWidth?: string;
  onClose?: () => void;
  keepOpenOnRouteChange?: boolean;
  /** Optional navigation list for prev/next browsing */
  navigationList?: SheetNavigationList;
  /** When navigationList is provided, this tracks the current item id */
  currentNavigationId?: string;
}
interface SheetManagerContextType {
  openSheet: (config: SheetConfig) => void;
  closeSheet: (id: string) => void;
  closeAllSheets: () => void;
}
const SheetManagerContext = createContext<SheetManagerContextType | undefined>(undefined);

/** Check if the active element is an input that should consume arrow keys */
function isInputFocused() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || (el as HTMLElement).isContentEditable;
}
export function SheetManagerProvider({
  children
}: {
  children: ReactNode;
}) {
  const [sheets, setSheets] = useState<SheetConfig[]>([]);
  const pathname = usePathname();
  const {
    SheetShell
  } = useSharedUI();

  // Close sheets on route change unless keepOpenOnRouteChange is true
  useEffect(() => {
    setSheets(prev => {
      const sheetsToClose = prev.filter(sheet => !sheet.keepOpenOnRouteChange);
      const sheetsToKeep = prev.filter(sheet_0 => sheet_0.keepOpenOnRouteChange);

      // Call onClose for sheets being closed
      sheetsToClose.forEach(sheet_1 => {
        if (sheet_1.onClose) {
          sheet_1.onClose();
        }
      });
      return sheetsToKeep;
    });
  }, [pathname]);
  const openSheet = useCallback((config: SheetConfig) => {
    setSheets(prev_0 => {
      // Replace if same ID exists
      const filtered = prev_0.filter(s => s.id !== config.id);
      return [...filtered, config];
    });
  }, []);
  const closeSheet = useCallback((id: string) => {
    setSheets(prev_1 => {
      const sheet_2 = prev_1.find(s_0 => s_0.id === id);
      if (sheet_2?.onClose) {
        sheet_2.onClose();
      }
      return prev_1.filter(s_1 => s_1.id !== id);
    });
  }, []);
  const closeAllSheets = useCallback(() => {
    setSheets(prev_2 => {
      prev_2.forEach(sheet_3 => {
        if (sheet_3.onClose) {
          sheet_3.onClose();
        }
      });
      return [];
    });
  }, []);
  const navigateSheet = useCallback((sheetId: string, direction: 'prev' | 'next') => {
    setSheets(prev_3 => prev_3.map(sheet_4 => {
      if (sheet_4.id !== sheetId || !sheet_4.navigationList) return sheet_4;
      const {
        items,
        renderContent
      } = sheet_4.navigationList;
      const currentIdx = items.findIndex(item => item.id === sheet_4.currentNavigationId);
      if (currentIdx === -1) return sheet_4;
      const nextIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
      // Wrap around to the other end of the list
      const wrappedIdx = nextIdx < 0 ? items.length - 1 : nextIdx >= items.length ? 0 : nextIdx;
      const nextItem = items[wrappedIdx];
      return {
        ...sheet_4,
        title: nextItem.title,
        content: renderContent(nextItem),
        currentNavigationId: nextItem.id
      };
    }));
  }, []);

  // Stable ref for sheets so the keyboard handler always sees the latest state
  const sheetsRef = useRef(sheets);
  sheetsRef.current = sheets;

  // Keyboard navigation: ArrowLeft/ArrowRight to navigate the topmost navigable sheet
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (isInputFocused()) return;
      const currentSheets = sheetsRef.current;
      // Find the topmost sheet with a navigation list
      const navSheet = [...currentSheets].reverse().find(s_2 => s_2.navigationList && s_2.navigationList.items.length > 1);
      if (!navSheet) return;
      const direction_0 = e.key === 'ArrowLeft' ? 'prev' : 'next';
      navigateSheet(navSheet.id, direction_0);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateSheet]);
  return <SheetManagerContext.Provider value={{
    openSheet,
    closeSheet,
    closeAllSheets
  }}>
      {children}
      {sheets.map(sheet_5 => {
      const navList = sheet_5.navigationList;
      const currentIdx_0 = navList ? navList.items.findIndex(item_0 => item_0.id === sheet_5.currentNavigationId) : -1;
      const hasPrev = navList && navList.items.length > 1;
      const hasNext = navList && navList.items.length > 1;
      const showNav = navList && navList.items.length > 1;
      return <SheetShell key={sheet_5.id} open={true} onOpenChange={open => {
        if (!open) {
          closeSheet(sheet_5.id);
        }
      }} className={`${sheet_5.maxWidth || 'sm:max-w-2xl'} overflow-y-auto`} title={sheet_5.title} headerActions={showNav ? <div className="flex items-center gap-1 mr-1">
                  <button onClick={() => navigateSheet(sheet_5.id, 'prev')} disabled={!hasPrev} className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors" aria-label="Previous item">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => navigateSheet(sheet_5.id, 'next')} disabled={!hasNext} className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors" aria-label="Next item">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  {currentIdx_0 >= 0 && <span className="text-xs text-gray-400 ml-1">
                      {currentIdx_0 + 1}/{navList!.items.length}
                    </span>}
                </div> : null}>
            <div className="flex-1 overflow-y-auto p-2">
              <div className="flex flex-col">
                <div className="p-2">{sheet_5.content}</div>
              </div>
            </div>
          </SheetShell>;
    })}
    </SheetManagerContext.Provider>;
}
export function useSheetManager() {
  const context = useContext(SheetManagerContext);
  if (!context) {
    throw new Error("useSheetManager must be used within SheetManagerProvider");
  }
  return context;
}